import { useContext, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Drawer, Typography } from '@loomstateorg/shared-frontend';
import Button from 'components/commercetools-ui/Button';
import { Market } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/types';
import FlagIcons from 'components/icons/flags';
import { MarketContext } from 'context/market';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';

interface MarketButtonProps {
  drawerClassName?: string;
}

const MarketButton = ({ drawerClassName = 'w-[90%] max-w-380 bg-neutral-200' }: MarketButtonProps) => {
  const [showMarket, setShowMarket] = useState(false);
  const { market: selectedMarket, markets, handleMarket } = useContext(MarketContext);

  const { formatMessage } = useFormat({ name: 'common' });

  const showMarketMenu = () => {
    setShowMarket(true);
  };

  const hideMarketMenu = () => {
    setShowMarket(false);
  };

  const handleMarketClick = (market: Market) => {
    handleMarket(market);
    setShowMarket(false);
  };

  return (
    <div className="hidden justify-center lg:flex" data-testid="market-selector">
      {selectedMarket && (
        <Button
          variant="ghost"
          onClick={showMarketMenu}
          className="flex w-fit items-center px-0 py-4"
          data-testid="open-market-menu"
        >
          <FlagIcons flagName={selectedMarket?.flag} className="mr-3 h-16 w-24" />
          <Typography
            fontSize={14}
            className="ml-5 font-normal text-neutral-100 hover:underline"
            data-testid="selected-market"
          >
            {selectedMarket?.region}
          </Typography>
        </Button>
      )}

      <Drawer
        isOpen={showMarket}
        direction="right"
        className={drawerClassName}
        onClose={hideMarketMenu}
      >
        <div
          className="flex w-full items-center justify-between border-b border-neutral-400 px-16 py-20"
          data-testid="market-menu-header"
        >
          <Typography as="h5" fontFamily="libre" fontSize={22} className="text-primary-regular">
            {formatMessage({ id: 'select.country', defaultMessage: 'Select your country' })}
          </Typography>
          <Button
            variant="ghost"
            onClick={hideMarketMenu}
            title={formatMessage({ id: 'close', defaultMessage: 'Close' })}
            aria-label={formatMessage({ id: 'close', defaultMessage: 'Close' })}
            className="p-0"
            data-testid="close"
          >
            <XMarkIcon className="w-28 text-secondary-regular" />
          </Button>
        </div>

        <div className="pt-20" data-testid="market-list">
          {markets.map((market, index) => (
            <Button
              key={market.flag}
              variant="ghost"
              onClick={() => handleMarketClick(market)}
              className="ml-5 flex w-full items-center justify-start py-14"
              data-testid={`market-option-${index}`}
            >
              {selectedMarket?.region === market?.region && <CheckIcon className="ml-5 mr-11 w-20" />}
              <FlagIcons flagName={market.flag} className="mr-8 h-20 w-32" />
              <Typography fontSize={16} className="font-normal text-primary-regular">
                {market.region}
              </Typography>
            </Button>
          ))}
        </div>
      </Drawer>
    </div>
  );
};
export default MarketButton;
